import { BrowserModule } from "@angular/platform-browser";
import { NgModule } from "@angular/core";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { DndDirective } from "./dnd.directive";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from "@angular/common/http";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { registerLocaleData } from "@angular/common";
import en from "@angular/common/locales/en";
import ro from "@angular/common/locales/ro";

import { ChartsModule } from "ng2-charts";
import { JwtInterceptor, ErrorInterceptor, Globals } from "./_helpers";
import { HomeComponent } from "./pages/home";
import { FilesComponent } from "./pages/files/files-list/files.component";
import { FileComponent } from "./pages/files/file-view/file.component";
import { FileDiagnosticComponent } from "./pages/files/file-diagnostic/file-diagnostic.component";
import { UploadComponent } from "./pages/upload/upload.component";
import { PayComponent } from "./pages/pay/pay.component";
import { ThankYouComponent } from "./pages/thank-you/thank-you.component";
import { UsersComponent } from "./pages/users/users.component";
import { LoginComponent } from "./pages/auth/login";
import { EditComponent } from "./pages/edit/edit.component";
import { RegisterComponent } from "./pages/auth/register/register.component";
import { AuthentificationComponent } from "./pages/auth/authentification/authentification.component";

registerLocaleData(ro);

import { NZ_I18N, ro_RO } from "ng-zorro-antd/i18n";
import { NzLayoutModule } from "ng-zorro-antd/layout";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzGridModule } from "ng-zorro-antd/grid";
import { NzTypographyModule } from "ng-zorro-antd/typography";
import { NzUploadModule } from "ng-zorro-antd/upload";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzListModule } from "ng-zorro-antd/list";
import { NzTableModule } from "ng-zorro-antd/table";
import { NzAlertModule } from "ng-zorro-antd/alert";
import { NzMenuModule } from "ng-zorro-antd/menu";
import { NzCollapseModule } from "ng-zorro-antd/collapse";
import { NzTagModule } from "ng-zorro-antd/tag";
import { NzResultModule } from "ng-zorro-antd/result";
import { NzStatisticModule } from "ng-zorro-antd/statistic";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzPopconfirmModule } from "ng-zorro-antd/popconfirm";

// Firebase services + enviorment module
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireStorageModule } from "@angular/fire/compat/storage";
import { environment } from "../environments/environment";
import { PasswordResetComponent } from "./pages/auth/password-reset/password-reset.component";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzInputNumberModule } from "ng-zorro-antd/input-number";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzMessageModule } from "ng-zorro-antd/message";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzProgressModule } from "ng-zorro-antd/progress";
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzToolTipModule } from "ng-zorro-antd/tooltip";
import { NzCalendarModule } from "ng-zorro-antd/calendar";
import { GoogleChartsModule } from "angular-google-charts";
import { QuillModule } from "ngx-quill";
import { MobilpayComponent } from "./pages/mobilpay/mobilpay.component";
import { NzRadioModule } from "ng-zorro-antd/radio";
import { ForgotPasswordComponent } from "./pages/auth/forgot-password/forgot-password.component";
import { NzPaginationModule } from "ng-zorro-antd/pagination";
import { ActivateAccountComponent } from "./pages/auth/activate-account/activate-account.component";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { NzCheckboxModule } from "ng-zorro-antd/checkbox";
import { ErrorFilterPipe } from "./_pipes/error-filter";
import { MomentDatePipe } from "./_pipes/momentdate";
import { FileSimpleComponent } from "./pages/files/file-simple/file-simple.component";
import { FileCompareComponent } from "./pages/files/file-compare/file-compare.component";
import { NzDatePickerModule } from "ng-zorro-antd/date-picker";
import { ChartsComponent } from "./pages/files/charts/charts.component";
import { NzBadgeModule } from "ng-zorro-antd/badge";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { SubscriptionsComponent } from "./pages/subscriptions/subscriptions.component";
import { BudgetComponent } from "./pages/budget/budget.component";
import { BudgetComparisonComponent } from "./pages/budget/budget-comparison/budget-comparison.component";
import { BudgetListComponent } from "./pages/budget/budget-list/budget-list.component";
import { TwoFactorSetupComponent } from "./pages/two-factor-setup/two-factor-setup.component";

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    LoginComponent,
    RegisterComponent,
    AuthentificationComponent,
    PasswordResetComponent,
    ForgotPasswordComponent,
    FilesComponent,
    UsersComponent,
    UploadComponent,
    PayComponent,
    ThankYouComponent,
    FileComponent,
    FileDiagnosticComponent,
    EditComponent,
    DndDirective,
    MobilpayComponent,
    ActivateAccountComponent,
    ErrorFilterPipe,
    MomentDatePipe,
    FileSimpleComponent,
    FileCompareComponent,
    ChartsComponent,
    SubscriptionsComponent,
    TwoFactorSetupComponent,
    BudgetComponent,
    BudgetComparisonComponent,
    BudgetListComponent
  ],
  imports: [
    BrowserModule,
    ReactiveFormsModule,
    HttpClientModule,
    AppRoutingModule,
    FormsModule,
    FormsModule,
    BrowserAnimationsModule,
    ChartsModule,
    NzLayoutModule,
    NzButtonModule,
    NzCheckboxModule,
    NzInputModule,
    NzInputNumberModule,
    NzFormModule,
    NzGridModule,
    NzTypographyModule,
    NzUploadModule,
    NzIconModule,
    NzListModule,
    NzTableModule,
    NzAlertModule,
    NzMenuModule,
    NzCollapseModule,
    NzMessageModule,
    NzSpinModule,
    NzProgressModule,
    NzModalModule,
    NzToolTipModule,
    NzCalendarModule,
    NzResultModule,
    NzSelectModule,
    NzPaginationModule,
    NzPopconfirmModule,
    QuillModule.forRoot(),
    NzTagModule,
    NzStatisticModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireStorageModule,
    GoogleChartsModule,
    NzRadioModule,
    NzDropDownModule,
    NzDatePickerModule,
    NzBadgeModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [Globals, { provide: NZ_I18N, useValue: ro_RO }, { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true }, { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true }],
  bootstrap: [AppComponent],
})
export class AppModule { }
